import { useState } from "react";
import { C } from "./constants";

// ── BARAKAH BUDGET — AUTOMATED TEST RUNNER ────────────────────
// Mounts as a floating panel. Tests localStorage, data integrity,
// and feature logic without touching the real app state.

const KEYS = {
  tx:       "bb_transactions_v1",
  debts:    "bb_debts_v1",
  recur:    "bb_recurring_v1",
  goals:    "bb_goals_v1",
  zakat:    "bb_zakat_v1",
  nw:       "bb_networth_v1",
  settings: "bb_settings_v1",
  recurSt:  "bb_recur_status_v1",
  demo:     "bb_demo_loaded",
};

const load = (key) => { try { return JSON.parse(localStorage.getItem(key) ?? "null"); } catch { return null; } };
const fmt  = (n) => "$" + Math.abs(n).toFixed(2);

// ── TEST DEFINITIONS ──────────────────────────────────────────
const TESTS = [

  // ── STORAGE ──────────────────────────────────────────────────
  {
    group: "Storage",
    name: "All localStorage keys exist",
    run: () => {
      const optional = ["recurSt"]; // created only after first recurring interaction
      const missing = Object.entries(KEYS)
        .filter(([n, k]) => !optional.includes(n) && localStorage.getItem(k) === null)
        .map(([n]) => n);
      if (missing.length) throw new Error(`Missing keys: ${missing.join(", ")}`);
    }
  },
  {
    group: "Storage",
    name: "Settings saved (name + currency)",
    run: () => {
      const s = load(KEYS.settings);
      if (!s) throw new Error("No settings found");
      if (!s.name) throw new Error("Name not set");
      if (!s.currency) throw new Error("Currency not set");
    }
  },
  {
    group: "Storage",
    name: "Transactions are valid array",
    run: () => {
      const txns = load(KEYS.tx);
      if (!Array.isArray(txns)) throw new Error("Not an array");
      if (txns.length === 0) throw new Error("No transactions (load demo data first)");
      const bad = txns.filter(t => !t.id || !t.date || t.amount === undefined);
      if (bad.length) throw new Error(`${bad.length} invalid transactions`);
    }
  },
  {
    group: "Storage",
    name: "Debts data structure valid",
    run: () => {
      const d = load(KEYS.debts);
      if (!d) throw new Error("No debt data");
      if (!Array.isArray(d.debts)) throw new Error("debts.debts not array");
      if (!Array.isArray(d.payments)) throw new Error("debts.payments not array");
    }
  },
  {
    group: "Storage",
    name: "Goals are valid array",
    run: () => {
      const goals = load(KEYS.goals);
      if (!Array.isArray(goals)) throw new Error("Not an array");
      goals.forEach((g, i) => {
        if (!g.id) throw new Error(`Goal ${i} missing id`);
        if (!g.name) throw new Error(`Goal ${i} missing name`);
        if (g.target === undefined) throw new Error(`Goal ${i} missing target`);
        if (g.saved === undefined) throw new Error(`Goal ${i} missing saved`);
      });
    }
  },
  {
    group: "Storage",
    name: "Recurring entries valid",
    run: () => {
      const rec = load(KEYS.recur);
      if (!Array.isArray(rec)) throw new Error("Not an array");
      rec.forEach((r, i) => {
        if (!r.id) throw new Error(`Recurring ${i} missing id`);
        if (!r.name) throw new Error(`Recurring ${i} missing name`);
        if (!r.amount) throw new Error(`Recurring ${i} missing amount`);
        if (!r.dayOfMonth) throw new Error(`Recurring ${i} missing dayOfMonth`);
      });
    }
  },

  // ── TRANSACTIONS ─────────────────────────────────────────────
  {
    group: "Transactions",
    name: "Income transactions are positive",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const income = txns.filter(t => t.category === "Income");
      const bad = income.filter(t => t.amount <= 0);
      if (bad.length) throw new Error(`${bad.length} income txns with negative amount`);
    }
  },
  {
    group: "Transactions",
    name: "Expense transactions are negative",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const expenses = txns.filter(t => t.category !== "Income" && !t.isGoalContribution);
      const bad = expenses.filter(t => t.amount > 0);
      if (bad.length) throw new Error(`${bad.length} expense txns with positive amount`);
    }
  },
  {
    group: "Transactions",
    name: "All dates are valid YYYY-MM-DD",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const bad = txns.filter(t => !/^\d{4}-\d{2}-\d{2}$/.test(t.date));
      if (bad.length) throw new Error(`${bad.length} txns with invalid date format`);
    }
  },
  {
    group: "Transactions",
    name: "Goal contributions have goalId",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const contrib = txns.filter(t => t.isGoalContribution);
      const bad = contrib.filter(t => !t.goalId);
      if (bad.length) throw new Error(`${bad.length} goal contributions missing goalId`);
    }
  },

  // ── GOALS ────────────────────────────────────────────────────
  {
    group: "Goals",
    name: "Goal saved ≤ target",
    run: () => {
      const goals = load(KEYS.goals) || [];
      const over = goals.filter(g => g.saved > g.target);
      if (over.length) throw new Error(`${over.length} goals exceed target: ${over.map(g=>g.name).join(", ")}`);
    }
  },
  {
    group: "Goals",
    name: "Goal contributions balance with saved amounts",
    run: () => {
      const txns  = load(KEYS.tx) || [];
      const goals = load(KEYS.goals) || [];
      const errors = [];
      goals.forEach(g => {
        const contribTotal = txns
          .filter(t => t.isGoalContribution && t.goalId === g.id)
          .reduce((s, t) => s + Math.abs(t.amount), 0);
        // saved should be >= contribTotal (may have initial saved amount)
        if (contribTotal > g.saved + 0.01) {
          errors.push(`${g.name}: contributions (${fmt(contribTotal)}) > saved (${fmt(g.saved)})`);
        }
      });
      if (errors.length) throw new Error(errors.join("; "));
    }
  },

  // ── RECURRING ────────────────────────────────────────────────
  {
    group: "Recurring",
    name: "Recurring status keys are valid",
    run: () => {
      const status = load(KEYS.recurSt) || {};
      const bad = Object.entries(status).filter(([k, v]) => !["confirmed","skipped"].includes(v));
      if (bad.length) throw new Error(`${bad.length} invalid status values`);
    }
  },
  {
    group: "Recurring",
    name: "No duplicate recurring IDs",
    run: () => {
      const rec = load(KEYS.recur) || [];
      const ids = rec.map(r => r.id);
      const dupes = ids.filter((id, i) => ids.indexOf(id) !== i);
      if (dupes.length) throw new Error(`Duplicate IDs: ${dupes.join(", ")}`);
    }
  },

  // ── DEBTS ────────────────────────────────────────────────────
  {
    group: "Debts",
    name: "Debt balances are non-negative",
    run: () => {
      const { debts = [] } = load(KEYS.debts) || {};
      const bad = debts.filter(d => d.currentBalance < 0);
      if (bad.length) throw new Error(`${bad.length} debts with negative balance`);
    }
  },
  {
    group: "Debts",
    name: "Debt payments reference valid debt IDs",
    run: () => {
      const { debts = [], payments = [] } = load(KEYS.debts) || {};
      const ids = new Set(debts.map(d => d.id));
      const bad = payments.filter(p => !ids.has(p.debtId));
      if (bad.length) throw new Error(`${bad.length} payments with no matching debt`);
    }
  },

  // ── ZAKAT ────────────────────────────────────────────────────
  {
    group: "Zakat",
    name: "Zakat data structure valid",
    run: () => {
      const z = load(KEYS.zakat);
      if (!z) throw new Error("No zakat data");
      // Just needs to be an object
      if (typeof z !== "object") throw new Error("Not an object");
    }
  },

  // ── NET WORTH ────────────────────────────────────────────────
  {
    group: "Net Worth",
    name: "Net worth adjustments are valid",
    run: () => {
      const nw = load(KEYS.nw) || { adjustments: [] };
      if (!Array.isArray(nw.adjustments)) throw new Error("adjustments not array");
      nw.adjustments.forEach((a, i) => {
        if (a.amount === undefined) throw new Error(`Adjustment ${i} missing amount`);
        if (!a.note) throw new Error(`Adjustment ${i} missing note`);
      });
    }
  },

  // ── DEMO DATA ────────────────────────────────────────────────
  {
    group: "Demo",
    name: "Demo data covers 15 months",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const months = new Set(txns.map(t => t.date.slice(0,7)));
      if (months.size < 15) throw new Error(`Only ${months.size} months of data (need 15)`);
    }
  },
  {
    group: "Demo",
    name: "Demo has income + expenses each month",
    run: () => {
      const txns = load(KEYS.tx) || [];
      const seededMonths = [
        "2025-01","2025-02","2025-03","2025-04","2025-05","2025-06",
        "2025-07","2025-08","2025-09","2025-10","2025-11","2025-12",
        "2026-01","2026-02","2026-03",
      ];
      const byMonth = {};
      txns.forEach(t => {
        const m = t.date.slice(0,7);
        if (!seededMonths.includes(m)) return; // ignore stray months
        if (!byMonth[m]) byMonth[m] = { inc:0, exp:0 };
        if (t.amount > 0) byMonth[m].inc++;
        else byMonth[m].exp++;
      });
      const bad = seededMonths.filter(m => !byMonth[m] || byMonth[m].inc === 0 || byMonth[m].exp === 0);
      if (bad.length) throw new Error(`${bad.length} months missing income or expenses: ${bad.join(", ")}`);
    }
  },
];

// ── COMPONENT ─────────────────────────────────────────────────
export function TestRunner({ onClose }) {
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [done, setDone]       = useState(false);

  const runAll = async () => {
    setRunning(true);
    setDone(false);
    const out = [];
    for (const t of TESTS) {
      await new Promise(r => setTimeout(r, 30)); // let UI breathe
      try {
        t.run();
        out.push({ ...t, status: "pass" });
      } catch (e) {
        out.push({ ...t, status: "fail", error: e.message });
      }
      setResults([...out]);
    }
    setRunning(false);
    setDone(true);
  };

  const groups = [...new Set(TESTS.map(t => t.group))];
  const passed = results.filter(r => r.status === "pass").length;
  const failed = results.filter(r => r.status === "fail").length;

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border2}`, borderRadius:20,
        width:"100%", maxWidth:480, maxHeight:"85vh", display:"flex", flexDirection:"column",
        boxShadow:"0 24px 60px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ color:C.text, fontWeight:800, fontSize:16 }}>🧪 Test Runner</div>
            <div style={{ color:C.text3, fontSize:11, marginTop:2 }}>Barakah Budget — {TESTS.length} automated tests</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.text3, fontSize:18, cursor:"pointer" }}>✕</button>
        </div>

        {/* Score bar */}
        {done && (
          <div style={{ padding:"12px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1, background:C.surface2, borderRadius:99, height:8, overflow:"hidden" }}>
              <div style={{ width:`${(passed/TESTS.length)*100}%`, height:"100%", background: failed===0 ? C.green : C.amber, borderRadius:99, transition:"width 0.5s" }} />
            </div>
            <div style={{ color: failed===0 ? C.green : C.amber, fontWeight:800, fontSize:14, minWidth:60, textAlign:"right" }}>
              {passed}/{TESTS.length} passed
            </div>
          </div>
        )}

        {/* Results */}
        <div style={{ flex:1, overflowY:"auto", padding:"12px 20px" }}>
          {!running && !done && (
            <div style={{ textAlign:"center", padding:"40px 0" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🧪</div>
              <div style={{ color:C.text2, fontSize:14, marginBottom:6 }}>Ready to run {TESTS.length} tests</div>
              <div style={{ color:C.text3, fontSize:12 }}>Make sure demo data is loaded for best results</div>
            </div>
          )}

          {groups.map(group => {
            const groupResults = results.filter(r => r.group === group);
            if (groupResults.length === 0 && !running) return null;
            return (
              <div key={group} style={{ marginBottom:16 }}>
                <div style={{ color:C.text3, fontSize:10, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>{group}</div>
                {TESTS.filter(t => t.group === group).map(t => {
                  const r = results.find(r => r.name === t.name);
                  const status = r?.status;
                  return (
                    <div key={t.name} style={{
                      display:"flex", alignItems:"flex-start", gap:10, padding:"7px 10px",
                      borderRadius:8, marginBottom:4,
                      background: status==="pass" ? "rgba(61,214,140,0.06)" : status==="fail" ? "rgba(240,82,82,0.08)" : "transparent",
                    }}>
                      <div style={{ fontSize:13, marginTop:1, minWidth:16 }}>
                        {status === "pass" ? "✅" : status === "fail" ? "❌" : running ? "⏳" : "○"}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ color: status==="pass" ? C.green : status==="fail" ? C.red : C.text2, fontSize:12, fontWeight:600 }}>{t.name}</div>
                        {r?.error && <div style={{ color:C.red, fontSize:11, marginTop:3, opacity:0.8 }}>{r.error}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 20px", borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
          <button onClick={runAll} disabled={running}
            style={{ flex:1, background: running ? C.surface2 : `linear-gradient(135deg,${C.green},#22c55e)`,
              border:"none", borderRadius:10, color: running ? C.text3 : "#0e0e13",
              fontSize:13, fontWeight:800, padding:"12px", cursor: running ? "default" : "pointer" }}>
            {running ? "Running tests…" : done ? "▶ Run Again" : "▶ Run All Tests"}
          </button>
          {done && failed > 0 && (
            <button onClick={() => setResults(results.filter(r => r.status==="fail"))}
              style={{ background:C.surface2, border:`1px solid ${C.border}`, color:C.text2, borderRadius:10, padding:"12px 14px", fontSize:12, cursor:"pointer" }}>
              Show failures only
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
